// Staff app API + local session + PIN/biometric helpers
import axios from "axios";

export const API = process.env.REACT_APP_BACKEND_URL;

const appApi = axios.create({ baseURL: `${API}/api` });

appApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("cfm_app_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Immediate kick: if the owner deactivates the account while in use, the app
// jumps straight to the deactivated screen instead of silently failing to load.
appApi.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || "";
    if (!url.includes("/app/auth/login")) {
      if (status === 403) window.dispatchEvent(new CustomEvent("cfm-app-deactivated"));
      else if (status === 401) window.dispatchEvent(new CustomEvent("cfm-app-unauthorized"));
    }
    throw err;
  }
);

export default appApi;

// ---- local session ----
export const saveSession = (token, user) => {
  localStorage.setItem("cfm_app_token", token);
  localStorage.setItem("cfm_app_user", JSON.stringify(user));
};
export const clearSession = () => {
  ["cfm_app_token", "cfm_app_user", "cfm_app_pin", "cfm_app_bio", "cfm_app_push"].forEach((k) => localStorage.removeItem(k));
};
export const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem("cfm_app_user") || "null"); } catch { return null; }
};

// ---- PIN (local quick unlock — the access code remains the real credential) ----
const sha256 = async (text) => {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
};
export const setPin = async (userId, pin) => localStorage.setItem("cfm_app_pin", await sha256(`${userId}:${pin}`));
export const checkPin = async (userId, pin) => localStorage.getItem("cfm_app_pin") === (await sha256(`${userId}:${pin}`));
export const hasPin = () => !!localStorage.getItem("cfm_app_pin");
export const clearPin = () => localStorage.removeItem("cfm_app_pin");

// ---- Biometrics (WebAuthn platform authenticator, local unlock gate) ----
const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0)).buffer;

export const bioAvailable = async () => {
  try {
    return !!(window.PublicKeyCredential && (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()));
  } catch { return false; }
};

export const bioEnroll = async (user) => {
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const cred = await navigator.credentials.create({
    publicKey: {
      challenge,
      rp: { name: "Care For Me" },
      user: { id: crypto.getRandomValues(new Uint8Array(16)), name: user.id, displayName: user.name },
      pubKeyCredParams: [{ alg: -7, type: "public-key" }, { alg: -257, type: "public-key" }],
      authenticatorSelection: { authenticatorAttachment: "platform", userVerification: "required" },
      timeout: 60000,
    },
  });
  if (!cred) throw new Error("enroll failed");
  localStorage.setItem("cfm_app_bio", b64(cred.rawId));
};

export const bioVerify = async () => {
  const idB64 = localStorage.getItem("cfm_app_bio");
  if (!idB64) return false;
  try {
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    const cred = await navigator.credentials.get({
      publicKey: {
        challenge,
        allowCredentials: [{ id: unb64(idB64), type: "public-key" }],
        userVerification: "required",
        timeout: 60000,
      },
    });
    return !!cred;
  } catch { return false; }
};
export const bioEnrolled = () => !!localStorage.getItem("cfm_app_bio");
export const bioClear = () => localStorage.removeItem("cfm_app_bio");

// ---- Daily rule: first unlock of the day requires the PIN, biometrics after that ----
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const markPinUnlockedToday = () => localStorage.setItem("cfm_app_pin_day", todayStr());
export const needsPinToday = () => localStorage.getItem("cfm_app_pin_day") !== todayStr();

// ---- Web push ----
export const pushSubscribed = () => localStorage.getItem("cfm_app_push") === "1";

export const pushSubscribe = async () => {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) throw new Error("unsupported");
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("denied");
  const key = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  const keyBytes = Uint8Array.from(atob(key.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(key.length / 4) * 4, "=")), (c) => c.charCodeAt(0));
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes });
  const json = sub.toJSON();
  await appApi.post("/app/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
  localStorage.setItem("cfm_app_push", "1");
};

export const pushUnsubscribe = async () => {
  try {
    const reg = await navigator.serviceWorker.getRegistration("/sw.js");
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      const json = sub.toJSON();
      await appApi.delete("/app/push/subscribe", { data: { endpoint: json.endpoint, keys: json.keys } });
      await sub.unsubscribe();
    }
  } finally {
    localStorage.removeItem("cfm_app_push");
  }
};

export const pushTest = () => appApi.post("/app/push/test");
